/**
 * Signup wizard — 3 quick steps:
 *   1. Country + base currency + FY start
 *   2. Company name + owner details
 *   3. Confirm & provision
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ChevronRight, Building2, Check } from 'lucide-react';
import { booksSignup, saveBooksSession, listBooksCountries, listBooksCurrencies, errMsg } from './api';

const BooksSignupPage = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({
    country_code: '', base_currency: '', fy_start_month: 4,
    company_name: '', owner_name: '', owner_email: '', owner_password: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [c, cu] = await Promise.all([listBooksCountries(), listBooksCurrencies()]);
        setCountries(c.data || []);
        setCurrencies(cu.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const selectedCountry = useMemo(
    () => countries.find((c) => c.code === form.country_code), [countries, form.country_code],
  );

  const onCountry = (code) => {
    const c = countries.find((x) => x.code === code);
    setForm((f) => ({
      ...f, country_code: code,
      base_currency: c?.default_currency || f.base_currency,
      fy_start_month: c?.fy_default_start_month || f.fy_start_month,
    }));
  };

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      const res = await booksSignup(form);
      saveBooksSession(res.token, res.user, res.tenant);
      nav('/books/app/dashboard');
    } catch (e) {
      setError(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#e2ede6] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-[#0b1a2e] text-white p-6">
          <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 32, fontWeight: 500, color: '#7bbf95' }}>Emerald Books</div>
          <div className="text-xs tracking-[0.3em] text-white/50 uppercase mt-1">Set up your company · Step {step} of 3</div>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

          {step === 1 && (
            <>
              <Field label="Country">
                <select value={form.country_code} onChange={(e) => onCountry(e.target.value)}
                  data-testid="signup-country"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  <option value="">Select country…</option>
                  {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Base currency">
                <select value={form.base_currency} onChange={(e) => setForm((f) => ({ ...f, base_currency: e.target.value }))}
                  data-testid="signup-currency"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  <option value="">Select currency…</option>
                  {currencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                </select>
              </Field>
              <Field label="Financial year starts">
                <select value={form.fy_start_month} onChange={(e) => setForm((f) => ({ ...f, fy_start_month: parseInt(e.target.value, 10) }))}
                  data-testid="signup-fy-month"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm">
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </Field>
              {selectedCountry && (
                <div className="text-xs text-[#0b1a2e]/60 bg-[#f7fbf8] rounded p-3 border border-[#0f5c3d]/10">
                  <Check className="w-3 h-3 inline mr-1 text-[#0f5c3d]" />
                  Standard Chart of Accounts + tax codes for {selectedCountry.name} will be pre-loaded.
                </div>
              )}
              <button onClick={() => setStep(2)} disabled={!form.country_code || !form.base_currency}
                data-testid="signup-next-1"
                className="w-full py-2.5 bg-[#0f5c3d] text-white rounded-full font-semibold disabled:opacity-50">
                Continue <ChevronRight className="w-4 h-4 inline ml-1" />
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <Field label="Company name">
                <input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                  data-testid="signup-company"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="Acme Ltd" />
              </Field>
              <Field label="Your name">
                <input value={form.owner_name} onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))}
                  data-testid="signup-owner-name"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </Field>
              <Field label="Work email">
                <input type="email" value={form.owner_email} onChange={(e) => setForm((f) => ({ ...f, owner_email: e.target.value }))}
                  data-testid="signup-email"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </Field>
              <Field label="Password">
                <input type="password" value={form.owner_password} onChange={(e) => setForm((f) => ({ ...f, owner_password: e.target.value }))}
                  data-testid="signup-password" minLength={8}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="8+ characters" />
              </Field>
              <div className="flex gap-2">
                <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Back</button>
                <button onClick={() => setStep(3)}
                  disabled={!form.company_name || !form.owner_name || !form.owner_email || form.owner_password.length < 8}
                  data-testid="signup-next-2"
                  className="flex-1 py-2.5 bg-[#0f5c3d] text-white rounded-full font-semibold disabled:opacity-50">
                  Review
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="border border-[#0f5c3d]/15 rounded-xl p-4 space-y-2 text-sm bg-[#f7fbf8]">
                <Row k="Company" v={form.company_name} />
                <Row k="Country" v={selectedCountry?.name} />
                <Row k="Base currency" v={form.base_currency} />
                <Row k="FY starts" v={['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][form.fy_start_month]} />
                <Row k="Owner" v={`${form.owner_name} · ${form.owner_email}`} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setStep(2)} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Back</button>
                <button onClick={submit} disabled={busy} data-testid="signup-submit"
                  className="flex-1 py-2.5 bg-[#0f5c3d] text-white rounded-full font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
                  Create my books
                </button>
              </div>
            </>
          )}

          <div className="text-xs text-center text-[#0b1a2e]/50 pt-2">
            Already have an account? <Link to="/books/login" className="text-[#0f5c3d] font-semibold">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-semibold uppercase tracking-widest text-[#0b1a2e]/70 mb-1">{label}</div>
    {children}
  </label>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#0b1a2e]/60">{k}</span>
    <span className="text-[#0b1a2e] font-medium truncate">{v}</span>
  </div>
);

export default BooksSignupPage;
